/**
 * vue-cesium - https://github.com/zouyaoji/vue-cesium
 * Vue 2.x components for CesiumJS.
 *
 * @package VueCesium
 * @author zouyaoji <370681295@qq.com>
 * @version 2.1.6
 * @license MIT
 * @homepage https://zouyaoji.top/vue-cesium
 * @copyright (c) 2018-2020, zouyaoji <370681295@qq.com>
 */
import _typeof from '@babel/runtime-corejs2/helpers/esm/typeof';
import _Object$defineProperties from '@babel/runtime-corejs2/core-js/object/define-properties';
import _Promise from '@babel/runtime-corejs2/core-js/promise';
import _Object$getPrototypeOf from '@babel/runtime-corejs2/core-js/object/get-prototype-of';
import _Object$getOwnPropertyDescriptor from '@babel/runtime-corejs2/core-js/object/get-own-property-descriptor';
import _Object$keys from '@babel/runtime-corejs2/core-js/object/keys';
import _getIterator from '@babel/runtime-corejs2/core-js/get-iterator';
import _regeneratorRuntime from '@babel/runtime-corejs2/regenerator';
import services from './services';
import nameClassMap from '../utils/nameClassMap';
import specialProps from '../utils/specialProps';
import '../utils/log';

var VM_PROP = 'vm';
/**
 * Get the parent component. 获取 vc-viewer 组件。
 * @param {VueComponent} $component.
 */

var getParent = function getParent($component) {
  return !$component.cesiumClass && $component.$options.name !== 'vc-viewer' ? getParent($component.$parent) : $component;
};
/**
 * @vueMethods
 */


var methods = {
  /**
   * Load components asynchronously. 异步加载组件。
   * @returns {Promise<Object>} { Cesium, viewer, cesiumObject }
   */
  load: function load() {
    var _this = this;

    var createCesiumObject, mount, setPropWatchers, $parent, Cesium, viewer;
    return _regeneratorRuntime.async(function load$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            if (!this._mounted) {
              _context2.next = 2;
              break;
            }

            return _context2.abrupt("return", false);

          case 2:
            createCesiumObject = this.createCesiumObject, mount = this.mount, setPropWatchers = this.setPropWatchers;
            $parent = getParent(this.$parent);
            Cesium = this.Cesium = $parent.Cesium;
            viewer = this.viewer = $parent.viewer; // If you call the unload method to unload the component, the Cesium object of the parent component may be unloaded. You need to load the parent component first.
            // 如果调用过 unload 方法卸载组件，父组件的 Cesium 对象可能会被卸载 需要先加载父组件。

            if ($parent.cesiumObject) {
              _context2.next = 8;
              break;
            }

            return _context2.abrupt("return", $parent.load());

          case 8:
            // Register vue Watchers. 注册 Vue 侦听器。
            setPropWatchers(true);
            return _context2.abrupt("return", createCesiumObject().then(function _callee(cesiumObject) {
              return _regeneratorRuntime.async(function _callee$(_context) {
                while (1) {
                  switch (_context.prev = _context.next) {
                    case 0:
                      _this.originInstance = cesiumObject; // Load the created Cesium object. 加载创建的 Cesium 对象。

                      return _context.abrupt("return", mount().then(function () {
                        _this._mounted = true; // Trigger the component's 'ready' event. 触发该组件的 'ready' 事件。

                        _this.$emit('ready', {
                          Cesium: Cesium,
                          viewer: viewer,
                          cesiumObject: cesiumObject,
                          vm: _this
                        });

                        return {
                          Cesium: Cesium,
                          viewer: viewer,
                          cesiumObject: cesiumObject,
                          vm: _this
                        };
                      }));

                    case 2:
                    case "end":
                      return _context.stop();
                  }
                }
              });
            }));

          case 10:
          case "end":
            return _context2.stop();
        }
      }
    }, null, this);
  },

  /**
   * Unloading components asynchronously. 异步卸载组件。
   * @returns {Promise<Boolean>} returns true on success and false on failure. 成功返回 true，失败返回 false。
   */
  unload: function unload() {
    var _this2 = this;

    var _iteratorNormalCompletion, _didIteratorError, _iteratorError, _iterator, _step, $node;

    return _regeneratorRuntime.async(function unload$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            // If the component has subcomponents, you need to remove the subcomponents first. 如果该组件带有子组件，需要先移除子组件。
            _iteratorNormalCompletion = true;
            _didIteratorError = false;
            _iteratorError = undefined;
            _context4.prev = 3;
            _iterator = _getIterator((this.$slots.default || []).map(function (vnode) {
              return vnode.componentInstance;
            }).filter(function (cmp) {
              return !!cmp;
            }));

          case 5:
            if (_iteratorNormalCompletion = (_step = _iterator.next()).done) {
              _context4.next = 12;
              break;
            }

            $node = _step.value;
            _context4.next = 9;
            return _regeneratorRuntime.awrap($node.unload());

          case 9:
            _iteratorNormalCompletion = true;
            _context4.next = 5;
            break;

          case 12:
            _context4.next = 18;
            break;

          case 14:
            _context4.prev = 14;
            _context4.t0 = _context4["catch"](3);
            _didIteratorError = true;
            _iteratorError = _context4.t0;

          case 18:
            _context4.prev = 18;
            _context4.prev = 19;

            if (!_iteratorNormalCompletion && _iterator.return != null) {
              _iterator.return();
            }

          case 21:
            _context4.prev = 21;

            if (!_didIteratorError) {
              _context4.next = 24;
              break;
            }

            throw _iteratorError;

          case 24:
            return _context4.finish(21);

          case 25:
            return _context4.finish(18);

          case 26:
            return _context4.abrupt("return", this._mounted ? this.unmount().then(function _callee2() {
              return _regeneratorRuntime.async(function _callee2$(_context3) {
                while (1) {
                  switch (_context3.prev = _context3.next) {
                    case 0:
                      // Teardown the watchers. 注销 Vue 侦听器。
                      _this2.setPropWatchers(false);

                      _this2.originInstance = undefined;
                      _this2._mounted = false; // If the component cannot be rendered without the parent component, the parent component needs to be removed.
                      // 如果该组件的渲染和父组件是绑定在一起的，需要移除父组件。

                      return _context3.abrupt("return", _this2.renderByParent ? _this2.$parent.unload() : true);

                    case 4:
                    case "end":
                      return _context3.stop();
                  }
                }
              });
            }) : false);

          case 27:
          case "end":
            return _context4.stop();
        }
      }
    }, null, this, [[3, 14, 18, 26], [19,, 21, 25]]);
  },

  /**
   * Reload components asynchronously. 异步重载组件.
   * @returns {Promise<Boolean>} returns true on success and false on failure. 成功返回 true，失败返回 false。
   */
  reload: function reload() {
    var _this3 = this;

    return _regeneratorRuntime.async(function reload$(_context5) {
      while (1) {
        switch (_context5.prev = _context5.next) {
          case 0:
            return _context5.abrupt("return", this.unload().then(function () {
              return _this3.load();
            }));

          case 1:
          case "end":
            return _context5.stop();
        }
      }
    }, null, this);
  },

  /**
   * Create Cesium objects asynchronously. 异步创建 Cesium 对象。
   * @returns {Promise<Object>} return the Cesium object.
   */
  createCesiumObject: function createCesiumObject() {
    var $props, cesiumClass, transformProps, options;
    return _regeneratorRuntime.async(function createCesiumObject$(_context6) {
      while (1) {
        switch (_context6.prev = _context6.next) {
          case 0:
            $props = this.$props, cesiumClass = this.cesiumClass, transformProps = this.transformProps;
            options = transformProps($props);
            return _context6.abrupt("return", new Cesium[cesiumClass](options));

          case 3:
          case "end":
            return _context6.stop();
        }
      }
    }, null, this);
  },

  /**
   * Register or unregister the vue watchers.
   * @param {Boolean} register
   */
  setPropWatchers: function setPropWatchers(register) {
    var _this4 = this;

    if (register) {
      var $props = this.$props,
          specialPropsKeys = this.specialPropsKeys,
          cesiumClass = this.cesiumClass,
          cesiumObject = this.cesiumObject;

      if (!cesiumClass || !Cesium[cesiumClass]) {
        return;
      }

      var _constructor = Cesium[cesiumClass];
      var args = [];

      for (var i = 0; i < _constructor.length; i++) {
        args.push({});
      } // Create a temporary object to get the writability of the current Cesium object or the props on its prototype chain to
      // detect whether the component property responds dynamically or reloads the component when the property changes.
      // 创建一个临时对象来获取当前 Cesium 对象或它原型链上的 prop 的可写性，以检测属性改变时组件属性是动态响应还是重载组件。


      var instance = cesiumObject || applyToConstructor(_constructor, args);
      $props && _Object$keys($props).forEach(function (vueProp) {
        var cesiumProp = vueProp;

        if (vueProp === 'labelStyle' || vueProp === 'wmtsStyle') {
          cesiumProp = 'style';
        } else if (vueProp === 'bmKey') {
          cesiumProp = 'key';
        }

        var pd = instance && _Object$getOwnPropertyDescriptor(instance, cesiumProp);

        var pdProto = instance && _Object$getOwnPropertyDescriptor(_Object$getPrototypeOf(instance), cesiumProp);

        var hasSetter = pd && pd.writable || pdProto && pdProto.set; // 如果在vue文件中已经监听了改 props 这儿不再监听了
        // If you have listened to the props in the vue file, you will not add any more listeners here.

        if (_this4._watchers.filter(function (v) {
          return v.expression === vueProp;
        }).length > 0) {
          return;
        } // returns an unwatch function that stops firing the callback


        var unwatch = _this4.$watch(vueProp, function _callee3(val) {
          var _cesiumObject, newVal;

          return _regeneratorRuntime.async(function _callee3$(_context7) {
            while (1) {
              switch (_context7.prev = _context7.next) {
                case 0:
                  _context7.next = 2;
                  return _regeneratorRuntime.awrap(_this4.createPromise);

                case 2:
                  if (!hasSetter) {
                    _context7.next = 8;
                    break;
                  }

                  // Attributes are writable and directly respond to changes in attributes.
                  // 属性可写，直接动态响应属性的改变。
                  _cesiumObject = _this4.cesiumObject;

                  if (specialPropsKeys.indexOf(vueProp) !== -1 && specialProps[vueProp].handler) {
                    newVal = specialProps[vueProp].handler.call(_this4, val); // If an exclude condition has been defined for the object, such as "_callback", Cesium will automatically handle it internally and no longer need to be assigned.
                    // 如果对象已经定义了 exclude 条件，如已经定义了“_callback”，Cesium 内部会自动处理的 不用再赋值了。

                    if (!(Cesium.defined(_cesiumObject[cesiumProp]) && Cesium.defined(_cesiumObject[cesiumProp]._callback))) {
                      _cesiumObject[cesiumProp] = newVal;
                    }
                  } else {
                    _cesiumObject[cesiumProp] = val;
                  }

                  return _context7.abrupt("return", true);

                case 8:
                  return _context7.abrupt("return", _this4.reload());

                case 9:
                case "end":
                  return _context7.stop();
              }
            }
          });
        }, {
          deep: specialPropsKeys.indexOf(vueProp) !== -1 && specialProps[vueProp].deep
        });

        _this4.unwatchFns.push(unwatch);
      }); // Destroy temporary objects.
      // 销毁临时对象

      instance = undefined;
    } else {
      // Stops firing the callback.
      // 注销 watchers。
      this.unwatchFns.forEach(function (item) {
        return item();
      });
      this.unwatchFns = [];
    }
  },

  /**
   * Mount Cesium objects asynchronously. 异步挂载 Cesium 对象，即将 Cesium 对象添加到 viewer 中。虚方法，在各 vue 组件中实现。
   * @returns {Promise<Boolean>} 操作成功返回 true，失败返回 false。
   */
  mount: function mount() {
    return _regeneratorRuntime.async(function mount$(_context8) {
      while (1) {
        switch (_context8.prev = _context8.next) {
          case 0:
            return _context8.abrupt("return", true);

          case 1:
          case "end":
            return _context8.stop();
        }
      }
    });
  },

  /**
   * Unmount Cesium objects asynchronously. 异步卸载 Cesium 对象，即将 Cesium 对象从 viewer 移除。虚方法，在各 vue 组件中实现。
   * @returns {Promise<Boolean>} 操作成功返回 true，失败返回 false。
   */
  unmount: function unmount() {
    return _regeneratorRuntime.async(function unmount$(_context9) {
      while (1) {
        switch (_context9.prev = _context9.next) {
          case 0:
            return _context9.abrupt("return", true);

          case 1:
          case "end":
            return _context9.stop();
        }
      }
    });
  },

  /**
   * Get the injected object. 获取注入的对象。主要是为了获取父组件和父组件的 Cesium 对象。
   * @returns {Object}
   */
  getServices: function getServices() {
    return services.methods.getServices.call(this);
  },

  /**
   * 将 props 转换为 Cesium 对象
   * @param {Object} props
   */
  transformProps: function transformProps(props) {
    var _this5 = this;

    var specialPropsKeys = this.specialPropsKeys,
        isEmptyObj = this.isEmptyObj,
        cesiumClass = this.cesiumClass;
    var options = {};
    var graphics = ['billboard', 'box', 'corridor', 'cylinder', 'ellipse', 'ellipsoid', 'label', 'model', 'tileset', 'path', 'plane', 'point', 'polygon', 'polyline', 'polylineVolume', 'rectangle', 'wall'];
    props && _Object$keys(props).forEach(function (vueProp) {
      var cesiumProp = vueProp; // The properties of the following Cesium instance objects are HTML or Vue reserved words and require special handling.
      // 以下 Cesium 实例对象的属性是 HTML 或 Vue 保留字，需要特别处理一下。

      if (vueProp === 'labelStyle' || vueProp === 'wmtsStyle') {
        cesiumProp = 'style';
      } else if (vueProp === 'bmKey') {
        cesiumProp = 'key';
      }

      if (graphics.indexOf(cesiumProp) !== -1 && getClassName(props[vueProp]) !== 'undefined' && getClassName(props[vueProp]).indexOf('Graphics') === -1 && (cesiumClass === 'Entity' || cesiumClass.indexOf('DataSource') !== -1)) {
        options[cesiumProp] = _this5.transformProps(props[vueProp]);
      } else {
        options[cesiumProp] = specialPropsKeys.indexOf(vueProp) !== -1 && specialProps[vueProp].handler && (!isEmptyObj(props[vueProp]) || typeof props[vueProp] === 'function') ? specialProps[vueProp].handler.call(_this5, props[vueProp]) : props[vueProp];
      }
    }); // Remove empty objects to avoid initialization errors when Cesium objects are initialized with null values.
    // 移除空对象，避免 Cesium 对象初始化时传入空值导致初始化报错。

    this.removeNullItem(options);
    return options;
  },

  /**
   * The action before the component is loaded. 组件加载前的操作。
   */
  beforeLoad: function beforeLoad() {
    return _regeneratorRuntime.async(function beforeLoad$(_context10) {
      while (1) {
        switch (_context10.prev = _context10.next) {
          case 0:
            if (!this.$parent.nowaiting) {
              _context10.next = 4;
              break;
            }

            return _context10.abrupt("return", true);

          case 4:
            _context10.next = 6;
            return _regeneratorRuntime.awrap(this.$parent.createPromise);

          case 6:
          case "end":
            return _context10.stop();
        }
      }
    }, null, this);
  },

  /**
   * 获取 vc-viewer 组件
   */
  getParent: getParent
};
/**
 * VueCesium common minxin
 */

var common = {
  VM_PROP: VM_PROP,
  mixins: [services],
  methods: methods,
  data: function data() {
    return {
      unwatchFns: []
    };
  },
  created: function created() {
    var _this6 = this;

    this._mounted = false;
    this.cesiumClass = nameClassMap[this.$options.name];
    this.specialPropsKeys = _Object$keys(specialProps);
    var beforeLoad = this.beforeLoad,
        load = this.load;
    var $parent = getParent(this.$parent); // this._createPromise = Promise.resolve(beforeLoad()).then(() => load())

    this._createPromise = _Promise.resolve(beforeLoad()).then(function () {
      return new _Promise(function (resolve, reject) {
        var viewer = $parent.viewer;
        viewer && resolve(load());
        $parent.$on('ready', function () {
          resolve(load()); // .then(val => resolve(val))
          // .catch((error) => reject(new Error(`[VueCesium] ERROR: An error occurred during the initialization of the ${this.cesiumClass}!` + error)))
        });
      });
    });

    _Object$defineProperties(this, {
      createPromise: {
        enumerable: true,
        get: function get() {
          return _this6._createPromise;
        }
      },
      cesiumObject: {
        enumerable: true,
        get: function get() {
          return _this6.originInstance;
        }
      },
      mounted: {
        enumerable: true,
        get: function get() {
          return _this6._mounted;
        }
      }
    });
  },
  destroyed: function destroyed() {
    var _this7 = this;

    this.unload().then(function () {
      _this7.$emit('destroyed', _this7);
    });
  }
};
/**
 * Use the apply method to call the Cesium constructor to initialize the Cesium instance. 使用 apply 调用 Cesium 构造函数初始化 Cesium 实例。
 * @param {Function} constructor Cesium 构造函数。
 * @param {Array} argArray 构造函数参数。
 * @returns {Object} 返回 Cesium 实例。
 */

function applyToConstructor(constructor, argArray) {
  var instance;

  try {
    var args = [{}].concat(argArray); // 动态创建 Cesium 临时实例，大部分都能正常创建，但有部分参数是不可省略的，暂时没想到更好的办法
    // args[1].url = ''

    args[1].mapId = {};
    args[1].geometry = {};
    args[1].rectangle = Cesium.Rectangle.MAX_VALUE;
    args[1].polygonHierarchy = {};
    args[1].assetId = 1;
    args[1].layers = '';
    args[2] = new Cesium.PolylineCollection({});
    var FactoryFunction = constructor.bind.apply(constructor, args);
    instance = new FactoryFunction();
    args = undefined;
  } catch (e) {
  }

  return instance;
}

function getClassName(objClass) {
  if (objClass && objClass.constructor) {
    var strFun = objClass.constructor.toString();
    var className = strFun.substr(0, strFun.indexOf('('));
    className = className.replace('function', '');
    return className.replace(/(^\s*)|(\s*$)/ig, '');
  }

  return _typeof(objClass);
}

export default common;
